<template>
  <div id="workspace-app">
    <!-- Upload Progress Modal -->
    <div v-if="uploaderState || zipState" class="upload-progress-modal">
      <b-row>
        <b-col v-if="uploaderState">
          <h6 class="text-white mb-1">Active uploads</h6>
          <small>
            Image {{ uploaderCurrentImage }} of {{ uploader.files.length }}.
          </small>
          <b-progress height="5px" striped variant="success" class="mb-2"
            :max="uploader.files.length"
            :value="uploaderDone.length" />
          <small>
            Image upload progress: {{ uploadStatus.toFixed(2) }}%.
          </small>
          <b-progress height="5px" striped variant="success"
            :max="100"
            :value="uploadStatus" />
        </b-col>
        <b-col v-if="zipState && !uploaderState">
          <div v-if="!downloadZipState">
            <div class="d-flex flex-column align-items-center">
              <h6 class="text-white mb-1">Active archive extracting</h6>
              <b-spinner variant="warning" label="Extracting..."></b-spinner>
            </div>
          </div>
          <div v-else>
            <h6 class="text-white mb-1">Active archive processing</h6>
            <small>
              Image {{ downloadZipCurrent.processed }} of {{ downloadZipCurrent.totalFiles }}.
            </small>
            <b-progress height="5px" striped variant="success" class="mb-2"
              :max="downloadZipCurrent.totalFiles"
              :value="downloadZipCurrent.processed" />
            <small>
              Archive: 1 of {{ zipUploader.files.filter(f => f.status === 'processing').length }}.
            </small>
            <b-progress height="5px" striped variant="success"
              :max="zipUploader.files.filter(f => f.status === 'processing').length"
              :value="1" />
          </div>
        </b-col>
      </b-row>
    </div>

    <Header />
    <RightNavbar />
    <LeftPanel v-if="!isProjectSelectPage" />
    <CentralContent>
      <router-view />
    </CentralContent>

    <div class="footer-right">&copy; 2026 Copyright Envisionit Deep AI. All rights reserved.</div>
    <div class="footer-left">Version 1.5.0.1</div>

    <!-- Change Password Modal -->
    <b-modal
      v-model="showChangePasswordModal"
      size="lg"
      centered
      hide-footer
      title="Change Password"
      header-class="change-password-modal-header"
      body-class="change-password-modal-body"
      modal-class="change-password-modal"
      title-class="change-password-modal-title"
      header-close-content="<i class='fas fa-times'></i>">
      <b-form @submit.prevent="submitPasswordChange">
        <b-form-group id="password-group" label-for="current-password" class="mb-3">
          <b-form-input
            v-model="passwords.current"
            id="current-password"
            type="password"
            required
            placeholder="Please enter your current password"
          ></b-form-input>
        </b-form-group>
        <hr class="modal-divider" />
        <b-form-group id="new-password-group" label-for="new-password" class="mb-3">
          <b-form-input
            v-model="passwords.new"
            id="new-password"
            type="password"
            required
            placeholder="Please enter your new password"
          ></b-form-input>
        </b-form-group>
        <b-form-group id="repeat-password-group" label-for="repeat-password" class="mb-3">
          <b-form-input
            v-model="passwords.repeat"
            id="repeat-password"
            type="password"
            required
            placeholder="Please repeat your new password"
          ></b-form-input>
        </b-form-group>
        <div v-if="passwords.new !== passwords.repeat && passwords.repeat" class="validation-error">
          Passwords do not match.
        </div>
        <div v-if="passwords.current === passwords.new && passwords.new" class="validation-error">
          Current and new passwords cannot be the same.
        </div>
        <div class="modal-actions">
          <b-button type="button" class="btn-modal-cancel" @click="closeModal">Cancel</b-button>
          <b-button :disabled="formStatus === 'submitting' || !canSubmit" type="submit" class="btn-modal-submit">
            {{ formStatus === 'submitting' ? 'Changing...' : 'Proceed' }}
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <!-- License Allocation Modal -->
    <b-modal
      v-model="showLicenseModal"
      size="lg"
      centered
      hide-footer
      title="License Allocation"
      header-class="license-modal-header"
      body-class="license-modal-body"
      modal-class="license-modal"
      title-class="license-modal-title"
      header-close-content="<i class='fas fa-times'></i>"
      v-if="licenseStatus && licenseStatus.loaded">
      <div class="license-section">
        <div class="license-row">
          <div class="license-label">Licenses:</div>
          <div class="license-value">
            <span v-for="_license in license.info.licenseKeys" :key="_license.id">
              <b>{{ _license.id }}</b><small>, expires on <b>{{ formatDate(_license.expiry) }}</b></small><br />
            </span>
          </div>
        </div>

        <hr class="modal-divider" />

        <div class="license-row">
          <div class="license-label">User Allocations:</div>
          <div class="license-value">
            <div class="allocation-item">
              <span>Available user licenses:</span>
              <b>{{ license.info.licensedUsers === 0 ? 'Unlimited' : license.info.licensedUsers }}</b> <small>users</small>
            </div>
            <div class="allocation-item">
              <span>Allocated user licenses:</span>
              <b>{{ license.info.allocatedUsers }}</b> <small>users</small>
            </div>
            <div class="allocation-item" v-if="false">
              <span>Total system users:</span>
              <b>{{ license.info.totalUsers }}</b> <small>users</small>
            </div>
            <div class="allocation-item">
              <span>Available user licenses:</span>
              <b>{{ license.info.availableUsers >= 0 ? license.info.availableUsers : 'Unlimited' }}</b> <small>users</small>
            </div>
            <div class="allocation-item allocation-warning" v-if="license.info.userOverallocation > 0">
              <span>Overallocated user licenses:</span>
              <b>{{ license.info.userOverallocation }}</b> <small>users</small>
            </div>
          </div>
        </div>

        <hr class="modal-divider" />

        <div class="license-row">
          <div class="license-label">Project Allocations:</div>
          <div class="license-value">
            <div class="allocation-item">
              <span>Available Project licenses:</span>
              <b>{{ license.info.licensedProjects === 0 ? 'Unlimited' : license.info.licensedProjects }}</b> <small>Projects</small>
            </div>
            <div class="allocation-item">
              <span>Allocated Project licenses:</span>
              <b>{{ license.info.allocatedProjects }}</b> <small>Projects</small>
            </div>
            <div class="allocation-item">
              <span>Available Project licenses:</span>
              <b>{{ license.info.availableProjects >= 0 ? license.info.availableProjects : 'Unlimited' }}</b> <small>Projects</small>
            </div>
            <div class="allocation-item allocation-warning" v-if="license.info.projectOverallocation > 0">
              <span>Overallocated Project licenses:</span>
              <b>{{ license.info.projectOverallocation }}</b> <small>Projects</small>
            </div>
          </div>
        </div>

        <hr class="modal-divider" />

        <div class="license-row">
          <div class="license-label">Licensed users:</div>
          <div class="license-value">
            <span v-for="_user in licensedUsers" :key="_user._id">
              <b>{{ _user.name }}</b><br />
            </span>
          </div>
        </div>

        <div v-if="loadLicense" class="new-license-section">
          <hr class="modal-divider" />
          <b-form-group id="license-group" label-for="license">
            <b-form-textarea
              v-model="newLicense.license"
              id="license"
              required
              rows="3"
              max-rows="6"
              placeholder="Please enter license string provided"
            ></b-form-textarea>
          </b-form-group>
          <b-form-group id="active-group" label-for="active">
            <b-form-checkbox
              v-model="newLicense.active"
              id="license-checkbox"
              required
              :value="true"
              :unchecked-value="false"
            >Activate license immediately</b-form-checkbox>
          </b-form-group>
        </div>

        <div class="modal-actions">
          <b-button v-if="!loadLicense" type="button" class="btn-modal-submit" @click="loadLicense = true">
            Load new license
          </b-button>
          <template v-if="loadLicense">
            <b-button type="button" class="btn-modal-cancel" @click="loadLicense = false">Cancel</b-button>
            <b-button :disabled="!newLicense.license" type="button" class="btn-modal-submit" @click="saveLicense()">
              Save new license
            </b-button>
          </template>
        </div>
      </div>
    </b-modal>

    <!-- Create Project Modal -->
    <b-modal
      v-model="showCreateProjectModal"
      size="lg"
      centered
      hide-footer
      title="Create New Project"
      header-class="create-project-modal-header"
      body-class="create-project-modal-body"
      modal-class="create-project-modal"
      title-class="create-project-modal-title"
      header-close-content="<i class='fas fa-times'></i>">
      <CreateProjectForm @close="showCreateProjectModal = false" @created="handleProjectCreated" />
    </b-modal>

    <!-- Platform Disclaimer Modal -->
    <b-modal
      v-model="showDisclaimerModal"
      size="xl"
      centered
      hide-footer
      no-close-on-backdrop
      no-close-on-esc
      title="Disclaimer"
      header-class="disclaimer-modal-header"
      body-class="disclaimer-modal-body"
      modal-class="disclaimer-modal"
      title-class="disclaimer-modal-title">
      <div class="disclaimer-content">
        <b-row>
          <b-col cols="12" lg="3" class="disclaimer-column">
            <h6 class="disclaimer-heading">PLATFORM<br />DISCLAIMER</h6>
            <p>The information provided by Envisionit Deep AI ("Company", "we", "our", "us") on RATIFY AI Assurance Tool Software-as-a-Service (the "Platform") is for general informational purposes only. All information on the Platform is provided in good faith, however we make no representation or warranty of any kind, express or implied, regarding the accuracy, adequacy, validity, reliability, availability, or completeness of any information on the Platform.</p>
            <p>Under no circumstance shall we have any liability to you for any loss or damage of any kind incurred as a result of the use of the Platform or reliance on any information provided on or produced or published by the Platform. Your use of the Platform and your reliance on any information on the Platform is solely at your own risk.</p>
          </b-col>
          <b-col cols="12" lg="3" class="disclaimer-column">
            <h6 class="disclaimer-heading">PROFESSIONAL<br />DISCLAIMER</h6>
            <p>The services provided on the Platform are for the validation and monitoring of third-party AI solutions based on the information uploaded or provided to the Platform. All findings and/or reports generated by the Platform are based on a algorithmic analysis of the information uploaded or provided on the Platform using machine learning technology.</p>
            <p>The Platform therefore does not contain medical advice of any nature. The information is provided for general notification and is not a substitute for professional medical advice. Information produced or published by the Platform is intended to be used and must be used for notification purposes only and must be verified by an appropriately trained medical practitioner. Accordingly, before taking any actions based upon such information or relying on such information, you must consult an appropriately trained medical practitioner.</p>
            <p>The use or reliance of any information contained on this Platform is solely at your own risk.</p>
          </b-col>
          <b-col cols="12" lg="3" class="disclaimer-column">
            <h6 class="disclaimer-heading">LIMITATION<br />OF LIABILITY</h6>
            <p>The customer agrees and acknowledges that the Services do not give specific advice on individual patients and should only be used by authorised users acting within their clinical competencies. The Services are not a substitute for professional medical qualifications and are used at the customer's own risk.</p>
            <p>For the avoidance of doubt, Envisionit Deep AI is not liable for and does not guarantee compliance with any of the Customer's regulatory or legal obligations, including those which relate to patient care, medical advice, treatment, diagnosis and healthcare.</p>
            <p>The Customer assumes sole responsibility for results obtained from the use of the Services and the Documentation by the Customer, and for conclusions drawn from such use. Envisionit Deep AI shall have no liability for any damage caused by errors or omissions in any information or instructions, Images or scripts provided to Envisionit Deep AI by the Customer in connection with the Services, or any actions taken by Envisionit Deep AI at the Customer's direction.</p>
          </b-col>
          <b-col cols="12" lg="3" class="disclaimer-column">
            <h6 class="disclaimer-heading">&nbsp;<br /><br /></h6>
            <p>All warranties, representations, conditions and all other terms of any kind whatsoever implied by statute or common law are, to the fullest extent permitted by applicable law, excluded from this agreement; and the services are provided to the Customer on an "as is" basis.</p>
            <h6 class="disclaimer-heading">CONTACT US</h6>
            <p>Should you have any feedback, comments, requests for technical support or other inquiries, please contact us by email: <a href="mailto:info@envisionit.ai" class="disclaimer-link">info@envisionit.ai</a>.</p>
          </b-col>
        </b-row>
        <div class="disclaimer-actions">
          <button class="btn-disclaimer-logout" @click="handleLogout">
            Logout
          </button>
          <button class="btn-disclaimer-accept" @click="handleAcceptDisclaimer">
            Accept
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script setup>
import { onMounted, onBeforeUnmount, computed, ref, watch, nextTick } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import moment from 'moment'
import Header from '../components/Header.vue'
import LeftPanel from '../components/LeftPanel.vue'
import RightNavbar from '../components/RightNavbar.vue'
import CentralContent from '../components/CentralContent.vue'
import CreateProjectForm from '../components/ProjectManagement/CreateProjectForm.vue'

const store = useStore()
const route = useRoute()
const router = useRouter()
let intervalId = null

// Uploader state for progress modal
const uploaderActive = ref([])
const uploaderDone = ref([])
const zipInterval = ref(null)
const uploader = computed(() => store.state.uploader)
const uploaderState = computed(() => uploader.value.uploading)
const zipUploader = computed(() => store.state.zipUploader)
const zipState = computed(() => zipUploader.value.uploading)
const uploaderCurrentImage = computed(() => {
  if (uploaderState.value) {
    return uploaderDone.value.length < uploader.value.files.length
      ? uploaderDone.value.length + 1
      : uploaderDone.value.length
  }
  return 0
})
const uploadStatus = computed(() => {
  if (!uploaderActive.value || uploaderActive.value.length <= 0) {
    return 0
  } else {
    if (uploaderActive.value.length > 0) {
      let sum = 0
      uploaderActive.value.forEach(f => {
        sum += f.progress || 0
      })
      return sum / uploaderActive.value.length
    }
    return 0
  }
})

// ZIP extraction state
const downloadZipState = computed(() => {
  if (zipUploader.value.uploading) {
    const inProgress = zipUploader.value.files.filter(b => b.status !== 'completed' && b.status !== 'failed')
    return inProgress.length > 0
  }
  return false
})

const downloadZipCurrent = computed(() => {
  const records = zipUploader.value.files.filter(b => b.status !== 'completed' && b.status !== 'failed')
  if (downloadZipState.value && records.length > 0) {
    return records[0]
  }
  return null
})

// Change Password Modal State
const showChangePasswordModal = computed({
  get: () => store.state.workspacePanel.showChangePasswordModal,
  set: (value) => store.dispatch('workspacePanel/setShowChangePasswordModal', value)
})

const passwords = ref({
  current: null,
  new: null,
  repeat: null
})

const formStatus = ref(null)

// Computed property to check if form can be submitted
const canSubmit = computed(() => {
  if (!passwords.value.current) return false
  if (!passwords.value.new) return false
  if (passwords.value.current === passwords.value.new) return false
  if (!passwords.value.repeat) return false
  if (passwords.value.new !== passwords.value.repeat) return false
  return true
})

// Watch for password change status
const changeStatus = computed(() => store.state.auth?.status?.changed)
watch(changeStatus, (value) => {
  if (value === true) {
    closeModal()
  }
})

// Reset form status when passwords change
watch(() => passwords.value.current, () => { formStatus.value = null })
watch(() => passwords.value.new, () => { formStatus.value = null })
watch(() => passwords.value.repeat, () => { formStatus.value = null })

// Submit password change
const submitPasswordChange = () => {
  formStatus.value = 'submitting'
  store.dispatch('auth/change', passwords.value)
}

// Close modal and reset form
const closeModal = () => {
  showChangePasswordModal.value = false
  passwords.value = {
    current: null,
    new: null,
    repeat: null
  }
  formStatus.value = null
}

// License Modal State
const showLicenseModal = computed({
  get: () => store.state.workspacePanel.showLicenseModal,
  set: (value) => store.dispatch('workspacePanel/setShowLicenseModal', value)
})

// Create Project Modal State
const showCreateProjectModal = computed({
  get: () => store.state.workspacePanel.showCreateProjectModal,
  set: (value) => store.dispatch('workspacePanel/setShowCreateProjectModal', value)
})

// Handle project created event
const handleProjectCreated = (project) => {
  showCreateProjectModal.value = false

  // Select the new project and navigate to its dashboard
  if (project && project._id) {
    store.dispatch('projectSubmission/selectProject', project._id)
    router.push(`/${project.type}/${project._id}`)
  }
}

// Platform Disclaimer Modal State
const showDisclaimerModal = ref(false)

// Check if user has accepted the disclaimer
const checkUserAcceptance = () => {
  const userId = store.state.auth?.user?.id
  if (userId && localStorage.getItem(userId) !== null) {
    showDisclaimerModal.value = false
  } else {
    showDisclaimerModal.value = true
  }
}

// Handle disclaimer acceptance
const handleAcceptDisclaimer = () => {
  const userId = store.state.auth?.user?.id
  if (userId) {
    localStorage.setItem(userId, 'Accepted')
    showDisclaimerModal.value = false
  }
}

// Handle logout from disclaimer
const handleLogout = () => {
  store.dispatch('auth/logout')
  router.push('/login')
}

const loadLicense = ref(false)
const savedLicense = ref(false)

const newLicense = ref({
  license: null,
  active: true
})

// License data from store
const license = computed(() => store.state.license)
const licenseStatus = computed(() => store.state.license?.status)
const licenseStatusLoaded = computed(() => store.state.license?.status?.loaded)
const licenseStatusSaved = computed(() => store.state.license?.status?.saved)

// Sorted licensed users
const licensedUsers = computed(() => {
  if (licenseStatusLoaded.value) {
    const users = JSON.parse(JSON.stringify(license.value.info.licensedUserList))
    users.sort((a, b) => (a.name > b.name ? 1 : (a.name < b.name ? -1 : 0)))
    return users
  }
  return []
})

// Format date helper
const formatDate = (date) => {
  try {
    return moment(date).format('MMM Do, YYYY')
  } catch (error) {
    console.error(error)
    return date
  }
}

// Save new license
const saveLicense = () => {
  store.dispatch('license/update', newLicense.value)
}

// Watch for license save completion
watch(licenseStatusSaved, (value) => {
  if (value) {
    savedLicense.value = true
    store.dispatch('license/get')
  }
})

// Watch for license load completion after save
watch(licenseStatusLoaded, (value) => {
  if (value && savedLicense.value) {
    savedLicense.value = false
    newLicense.value = {
      license: null,
      active: true
    }
    loadLicense.value = false
    nextTick(() => {
      showLicenseModal.value = true
    })
  }
})

// Check if on ProjectSelect page (hide LeftPanel)
const isProjectSelectPage = computed(() => route.name === 'Workspace' || route.path === '/')

// Project initialization from route
const projects = computed(() => store.state.projectSubmission.projects || [])
const projectsLoading = computed(() => store.state.projectSubmission.loading)

const initializeProjectFromRoute = () => {
  const projectId = route.params.projectId

  // No projectId in route, nothing to initialize
  if (!projectId) {
    return
  }

  // Wait for projects to be loaded
  if (projectsLoading.value || projects.value.length === 0) {
    console.log('WorkspaceLayout: Projects not ready for initialization. Loading:', projectsLoading.value, 'Count:', projects.value.length)
    return
  }

  // Check if this project is already selected
  const currentSelectedId = store.state.projectSubmission.selectedProjectId
  if (currentSelectedId === projectId) {
    console.log('WorkspaceLayout: Project already selected:', projectId)
    return
  }

  // Find the project
  const project = projects.value.find(p => p._id === projectId)

  if (!project) {
    console.error('WorkspaceLayout: Project not found:', projectId)
    router.push('/')
    return
  }

  // Set selected project
  console.log('WorkspaceLayout: Setting selected project from route:', projectId)
  store.dispatch('projectSubmission/selectProject', projectId)
}

// Watch for route changes to initialize project
watch(() => route.params.projectId, (newProjectId, oldProjectId) => {
  if (newProjectId && newProjectId !== oldProjectId) {
    console.log('WorkspaceLayout: Route projectId changed:', oldProjectId, '->', newProjectId)
    initializeProjectFromRoute()
  }
}, { immediate: true })

// Watch for projects to be loaded
watch([projects, projectsLoading], () => {
  if (!projectsLoading.value && projects.value.length > 0 && route.params.projectId) {
    console.log('WorkspaceLayout: Projects loaded, initializing project from route')
    initializeProjectFromRoute()
  }
})

// Upload files method (handles files from uploader store)
const uploadFiles = async (files, maxConcurrent = 2) => {
  const activeUploads = new Set()
  const queue = [...files]
  const results = []

  const processFile = async (fileObj) => {
    uploaderActive.value.push(fileObj)

    try {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        const formData = new FormData()

        formData.append('file', fileObj.file)

        if (fileObj.data) {
          Object.entries(fileObj.data).forEach(([key, value]) => {
            if (value && typeof value === 'object') {
              formData.append(key, JSON.stringify(value))
            } else if (value !== null && value !== undefined) {
              formData.append(key, value)
            }
          })
        }

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded * 100) / event.total * 100) / 100
            const activeIndex = uploaderActive.value.findIndex(f => f.id === fileObj.id)
            if (activeIndex !== -1) {
              uploaderActive.value[activeIndex].progress = progress
            }
          }
        }

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            uploaderDone.value.push({ ...fileObj, response: xhr.status })
            const activeIndex = uploaderActive.value.findIndex(f => f.id === fileObj.id)
            if (activeIndex !== -1) {
              uploaderActive.value.splice(activeIndex, 1)
            }
            resolve({ status: xhr.status })
          } else {
            reject(new Error(`HTTP Error: ${xhr.status}`))
          }
        }

        xhr.onerror = () => reject(new Error('Network Error'))

        xhr.open('POST', fileObj.postAction)
        xhr.withCredentials = true

        if (fileObj.headers) {
          Object.entries(fileObj.headers).forEach(([key, value]) => {
            if (value && value !== 'Bearer ' && value !== 'Bearer undefined') {
              xhr.setRequestHeader(key, value)
            }
          })
        }

        xhr.send(formData)
      })
    } catch (error) {
      uploaderDone.value.push({ ...fileObj, response: { error: error.message, status: 'error' } })
      const activeIndex = uploaderActive.value.findIndex(f => f.id === fileObj.id)
      if (activeIndex !== -1) {
        uploaderActive.value.splice(activeIndex, 1)
      }
      throw error
    }
  }

  while (queue.length > 0 || activeUploads.size > 0) {
    while (queue.length > 0 && activeUploads.size < maxConcurrent) {
      const fileObj = queue.shift()
      const promise = processFile(fileObj)
        .then(result => {
          activeUploads.delete(promise)
          results.push(result)
        })
        .catch(error => {
          activeUploads.delete(promise)
          console.error(`Error uploading file ${fileObj.name}:`, error)
        })
      activeUploads.add(promise)
    }

    if (activeUploads.size >= maxConcurrent || queue.length === 0) {
      await Promise.race(activeUploads)
    }
  }

  return results
}

// Handle uploads when uploader state changes
const handleUploads = async () => {
  if (uploaderState.value) {
    try {
      const files = uploader.value.files
      const results = await uploadFiles(files)
      console.log(results, files)

      // Show success message
      store.dispatch('alert/success', 'All images have been uploaded.', { root: true })

      // Check for ZIP files and start polling if found
      const zipFiles = files.filter(f => f.name.endsWith('.zip'))
      console.log('Detected ZIP files:', zipFiles)
      if (zipFiles && zipFiles.length > 0) {
        // Enable ZIP upload state
        store.dispatch('zipUploader/setUpload', { val: true })
        // Start polling for extraction progress
        if (!zipInterval.value) {
          zipInterval.value = setInterval(() => {
            store.dispatch('zipUploader/poll', { pid: 123 })
          }, 5000) // Poll every 5 seconds
        }
      }

      // Clear uploader state
      store.dispatch('uploader/clear')
      uploaderActive.value = []
      uploaderDone.value = []

      // Refresh the image list from backend (unless ZIP is extracting)
      if (zipFiles.length === 0) {
        const currentProjectId = store.state.projectSubmission.selectedProjectId
        if (currentProjectId) {
          await store.dispatch('projectSubmission/getProjectImages2', currentProjectId)
          console.log('Image list refreshed after upload')
        }
      }
    } catch (error) {
      console.log(error)
      store.dispatch('alert/error', 'There was an error uploading images.', { root: true })
    }
  }
}

// Watch for uploader state changes
watch(uploaderState, () => {
  if (uploaderState.value) {
    if (uploader.value.files && uploader.value.files.length > 0) {
      handleUploads()
    }
  }
})

// Watch for ZIP extraction state changes
watch(downloadZipState, (newVal) => {
  console.log('ZIP extraction state changed:', newVal)
  if (newVal) {
    // ZIP extraction in progress - start polling if not already started
    if (!zipInterval.value) {
      zipInterval.value = setInterval(() => {
        store.dispatch('zipUploader/poll', { pid: 123 })
      }, 5000)
    }
  } else {
    // ZIP extraction complete - cleanup
    if (zipInterval.value) {
      clearInterval(zipInterval.value)
      zipInterval.value = null
    }
    // Disable ZIP upload state
    store.dispatch('zipUploader/setUpload', { val: false })

    // Refresh project images now that extraction is complete
    const currentProjectId = store.state.projectSubmission.selectedProjectId
    if (currentProjectId) {
      store.dispatch('projectSubmission/getProjectImages2', currentProjectId)
      console.log('Image list refreshed after ZIP extraction')
    }
  }
})

// Initialize theme and load projects on mount
onMounted(async () => {
  console.log('=== Workspace Layout Loaded ===')
  console.log('Auth Store:', store.state.auth)
  console.log('User:', store.state.auth?.user)
  console.log('Status:', store.state.auth?.status)
  console.log('===============================')

  // Check if user has accepted the disclaimer
  checkUserAcceptance()

  // Initialize theme
  store.dispatch('workspaceTheme/setTheme', 'slate')

  // Load license data
  if (!licenseStatus.value || !licenseStatus.value.loaded) {
    await store.dispatch('license/get')
  }

  // Load projects
  console.log('=== Loading Projects ===')
  await store.dispatch('projectSubmission/getUserProjects')

  // Log projects after loading
  console.log('Projects from store:', store.state.projectSubmission.projects)
  console.log('Projects count:', store.state.projectSubmission.projects?.length || 0)
  console.log('========================')

  // Set up polling interval (refresh every 30 seconds like original)
  intervalId = setInterval(async () => {
    await store.dispatch('projectSubmission/getUserProjects')
    console.log('Projects refreshed:', store.state.projectSubmission.projects?.length || 0)
  }, 30000)

  // Initial check for any in-progress ZIP extractions
  store.dispatch('zipUploader/poll', { pid: 123 })
})

// Clean up interval on unmount
onBeforeUnmount(() => {
  if (intervalId) {
    clearInterval(intervalId)
  }
  // Clean up ZIP polling interval
  if (zipInterval.value) {
    clearInterval(zipInterval.value)
    zipInterval.value = null
  }
})
</script>

<style scoped>
@import '../colors.css';

#workspace-app {
  min-height: 100vh;
  background-color: var(--color-bg-primary);
  transition: background-color 0.3s ease;
}
</style>

<style>
/* Change Password Modal Styling */
.change-password-modal .modal-content {
  background-color: var(--color-bg-secondary) !important;
  border: var(--border-width-standard) solid var(--color-border-primary) !important;
  border-radius: var(--border-radius-standard) !important;
  box-shadow: var(--shadow-standard) !important;
}

.change-password-modal-header {
  background-color: var(--color-bg-primary) !important;
  border-bottom: var(--border-width-standard) solid var(--color-border-primary) !important;
  border-radius: var(--border-radius-standard) var(--border-radius-standard) 0 0 !important;
  padding: 1.25rem 1.5rem !important;
}

.change-password-modal-title {
  color: var(--color-text-primary) !important;
  font-weight: var(--font-weight-bold) !important;
  font-size: 1.25rem !important;
  letter-spacing: var(--letter-spacing-header) !important;
}

.change-password-modal-header .btn-close {
  filter: brightness(0) invert(1);
  opacity: 0.8;
  transition: opacity 0.2s ease;
}

.change-password-modal-header .btn-close:hover {
  opacity: 1;
}

.change-password-modal-body {
  background-color: var(--color-bg-secondary) !important;
  color: var(--color-text-primary) !important;
  padding: 1.5rem !important;
}

/* Form Input Styling */
.change-password-modal-body input {
  background-color: var(--color-bg-primary) !important;
  border: 1px solid var(--color-border-secondary) !important;
  border-radius: var(--border-radius-small) !important;
  color: var(--color-text-primary) !important;
  padding: 0.75rem 1rem !important;
  font-size: 1rem !important;
  transition: all 0.2s ease !important;
}

.change-password-modal-body input:focus {
  background-color: var(--color-bg-primary) !important;
  border-color: var(--color-accent-primary) !important;
  box-shadow: var(--shadow-focus) !important;
  outline: none !important;
}

.change-password-modal-body input::placeholder {
  color: var(--color-text-secondary) !important;
  opacity: 0.6 !important;
}

/* Divider */
.modal-divider {
  border: 0;
  border-top: 1px solid var(--color-divider-navy);
  margin: 1.5rem 0;
}

/* Validation Error Messages */
.validation-error {
  background-color: rgba(220, 53, 69, 0.1);
  border-left: var(--border-width-card) solid var(--color-error);
  color: var(--color-error);
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  border-radius: var(--border-radius-small);
  font-size: 0.9rem;
  font-weight: var(--font-weight-semibold);
}

/* Modal Actions */
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
}

/* Modal Buttons */
.btn-modal-cancel,
.btn-modal-submit {
  padding: 0.625rem 1.5rem;
  font-size: 1rem;
  font-weight: var(--font-weight-semibold);
  border-radius: 2rem;
  transition: all 0.2s ease;
  border: none;
  cursor: pointer;
}

.btn-modal-cancel {
  background-color: var(--color-button-secondary);
  color: var(--color-text-primary);
}

.btn-modal-cancel:hover:not(:disabled) {
  background-color: #2a6ba8;
  transform: translateY(-1px);
  box-shadow: var(--shadow-elevated);
}

.btn-modal-submit {
  background-color: var(--color-accent-primary);
  color: var(--color-bg-primary);
  padding-left: 2rem;
  padding-right: 2rem;
}

.btn-modal-submit:hover:not(:disabled) {
  background-color: var(--color-accent-hover);
  transform: translateY(-1px);
  box-shadow: var(--shadow-elevated);
}

.btn-modal-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Remove Bootstrap default focus styles */
.change-password-modal-body .form-control:focus {
  box-shadow: var(--shadow-focus) !important;
}

/* License Allocation Modal Styling */
.license-modal .modal-content {
  background-color: var(--color-bg-secondary) !important;
  border: var(--border-width-standard) solid var(--color-border-primary) !important;
  border-radius: var(--border-radius-standard) !important;
  box-shadow: var(--shadow-standard) !important;
}

.license-modal-header {
  background-color: var(--color-bg-primary) !important;
  border-bottom: var(--border-width-standard) solid var(--color-border-primary) !important;
  border-radius: var(--border-radius-standard) var(--border-radius-standard) 0 0 !important;
  padding: 1.25rem 1.5rem !important;
}

.license-modal-title {
  color: var(--color-text-primary) !important;
  font-weight: var(--font-weight-bold) !important;
  font-size: 1.25rem !important;
  letter-spacing: var(--letter-spacing-header) !important;
}

.license-modal-header .btn-close {
  filter: brightness(0) invert(1);
  opacity: 0.8;
  transition: opacity 0.2s ease;
}

.license-modal-header .btn-close:hover {
  opacity: 1;
}

.license-modal-body {
  background-color: var(--color-bg-secondary) !important;
  color: var(--color-text-primary) !important;
  padding: 1.5rem !important;
}

.license-section {
  color: var(--color-text-primary);
}

.license-row {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 1.5rem;
  margin-bottom: 1rem;
}

.license-label {
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
  padding-top: 0.25rem;
}

.license-value {
  color: var(--color-text-primary);
}

.allocation-item {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--color-divider-navy);
}

.allocation-item:last-child {
  border-bottom: none;
}

.allocation-item span {
  color: var(--color-text-secondary);
  font-size: 0.9rem;
}

.allocation-item b {
  color: var(--color-accent-primary);
}

.allocation-warning {
  color: #fbbf24 !important;
}

.allocation-warning span,
.allocation-warning b {
  color: #fbbf24 !important;
}

.new-license-section {
  margin-top: 1rem;
}

/* License modal textarea styling */
.license-modal-body textarea {
  background-color: var(--color-bg-primary) !important;
  border: 1px solid var(--color-border-secondary) !important;
  border-radius: var(--border-radius-small) !important;
  color: var(--color-text-primary) !important;
  padding: 0.75rem 1rem !important;
  font-size: 0.9rem !important;
  font-family: monospace !important;
  transition: all 0.2s ease !important;
}

.license-modal-body textarea:focus {
  background-color: var(--color-bg-primary) !important;
  border-color: var(--color-accent-primary) !important;
  box-shadow: var(--shadow-focus) !important;
  outline: none !important;
}

.license-modal-body textarea::placeholder {
  color: var(--color-text-secondary) !important;
  opacity: 0.6 !important;
}

/* License modal checkbox styling */
.license-modal-body .form-check-input {
  background-color: var(--color-bg-primary) !important;
  border: 1px solid var(--color-border-secondary) !important;
  cursor: pointer;
}

.license-modal-body .form-check-input:checked {
  background-color: var(--color-accent-primary) !important;
  border-color: var(--color-accent-primary) !important;
}

.license-modal-body .form-check-input:focus {
  box-shadow: var(--shadow-focus) !important;
}

.license-modal-body .form-check-label {
  color: var(--color-text-primary) !important;
  cursor: pointer;
}

/* Create Project Modal Styling */
.create-project-modal .modal-content {
  background-color: var(--color-bg-secondary) !important;
  border: var(--border-width-standard) solid var(--color-border-primary) !important;
  border-radius: var(--border-radius-standard) !important;
  box-shadow: var(--shadow-standard) !important;
}

.create-project-modal-header {
  background-color: var(--color-bg-primary) !important;
  border-bottom: var(--border-width-standard) solid var(--color-border-primary) !important;
  border-radius: var(--border-radius-standard) var(--border-radius-standard) 0 0 !important;
  padding: 1.25rem 1.5rem !important;
}

.create-project-modal-title {
  color: var(--color-text-primary) !important;
  font-weight: var(--font-weight-bold) !important;
  font-size: 1.25rem !important;
  letter-spacing: var(--letter-spacing-header) !important;
}

.create-project-modal-header .btn-close {
  filter: brightness(0) invert(1);
  opacity: 0.8;
  transition: opacity 0.2s ease;
}

.create-project-modal-header .btn-close:hover {
  opacity: 1;
}

.create-project-modal-body {
  background-color: var(--color-bg-secondary) !important;
  color: var(--color-text-primary) !important;
  padding: 1.5rem !important;
}

/* Create Project Modal Form Styling */
.create-project-modal-body input,
.create-project-modal-body textarea,
.create-project-modal-body select {
  background-color: var(--color-bg-primary) !important;
  border: 1px solid var(--color-border-secondary) !important;
  border-radius: var(--border-radius-small) !important;
  color: var(--color-text-primary) !important;
  transition: all 0.2s ease !important;
}

.create-project-modal-body input:focus,
.create-project-modal-body textarea:focus,
.create-project-modal-body select:focus {
  background-color: var(--color-bg-primary) !important;
  border-color: var(--color-accent-primary) !important;
  box-shadow: var(--shadow-focus) !important;
  outline: none !important;
}

.create-project-modal-body input::placeholder,
.create-project-modal-body textarea::placeholder {
  color: var(--color-text-secondary) !important;
  opacity: 0.6 !important;
}

.create-project-modal-body label {
  color: var(--color-text-primary) !important;
}

.create-project-modal-body .form-check-input {
  background-color: var(--color-bg-primary) !important;
  border: 1px solid var(--color-border-secondary) !important;
  cursor: pointer;
}

.create-project-modal-body .form-check-input:checked {
  background-color: var(--color-accent-primary) !important;
  border-color: var(--color-accent-primary) !important;
}

.create-project-modal-body .form-check-label {
  color: var(--color-text-primary) !important;
  cursor: pointer;
}

/* Platform Disclaimer Modal Styling */
.disclaimer-modal .modal-content {
  background-color: var(--color-bg-secondary) !important;
  border: var(--border-width-standard) solid var(--color-border-primary) !important;
  border-radius: var(--border-radius-standard) !important;
  box-shadow: var(--shadow-standard) !important;
}

.disclaimer-modal-header {
  background-color: var(--color-bg-primary) !important;
  border-bottom: var(--border-width-standard) solid var(--color-border-primary) !important;
  border-radius: var(--border-radius-standard) var(--border-radius-standard) 0 0 !important;
  padding: 1.25rem 1.5rem !important;
}

.disclaimer-modal-title {
  color: var(--color-text-primary) !important;
  font-weight: var(--font-weight-bold) !important;
  font-size: 1.5rem !important;
  letter-spacing: var(--letter-spacing-header) !important;
  text-align: center;
  width: 100%;
}

.disclaimer-modal-body {
  background-color: var(--color-bg-secondary) !important;
  color: var(--color-text-primary) !important;
  padding: 2rem !important;
}

.disclaimer-content {
  color: var(--color-text-primary);
}

.disclaimer-column {
  margin-bottom: 1.5rem;
}

.disclaimer-heading {
  color: var(--color-text-primary) !important;
  font-weight: var(--font-weight-bold) !important;
  font-size: 1rem !important;
  letter-spacing: 0.05em;
  margin-bottom: 1rem;
  line-height: 1.4;
}

.disclaimer-column p {
  color: var(--color-text-secondary) !important;
  font-size: 0.875rem !important;
  line-height: 1.6;
  margin-bottom: 1rem;
  text-align: justify;
}

.disclaimer-link {
  color: var(--color-accent-primary) !important;
  text-decoration: none;
  transition: color 0.2s ease;
}

.disclaimer-link:hover {
  color: var(--color-accent-hover) !important;
  text-decoration: underline;
}

.disclaimer-actions {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--color-divider-navy);
}

.btn-disclaimer-logout,
.btn-disclaimer-accept {
  padding: 0.75rem 2rem;
  font-size: 1rem;
  font-weight: var(--font-weight-semibold);
  border-radius: 2rem;
  transition: all 0.2s ease;
  border: none;
  cursor: pointer;
}

.btn-disclaimer-logout {
  background-color: var(--color-error);
  color: var(--color-text-primary);
}

.btn-disclaimer-logout:hover {
  background-color: #c82333;
  transform: translateY(-1px);
  box-shadow: var(--shadow-elevated);
}

.btn-disclaimer-accept {
  background-color: var(--color-accent-primary);
  color: var(--color-bg-primary);
  padding-left: 2.5rem;
  padding-right: 2.5rem;
}

.btn-disclaimer-accept:hover {
  background-color: var(--color-accent-hover);
  transform: translateY(-1px);
  box-shadow: var(--shadow-elevated);
}

/* Responsive adjustments for disclaimer */
@media (max-width: 991.98px) {
  .disclaimer-column {
    margin-bottom: 2rem;
  }

  .disclaimer-heading {
    font-size: 1.1rem;
  }

  .disclaimer-column p {
    font-size: 0.9rem;
  }
}

/* Upload Progress Modal (migrated from global styles.css) */
.upload-progress-modal {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  background-color: rgba(20, 48, 76, 0.95);
  border: 2px solid var(--color-border-primary);
  border-radius: 12px;
  padding: 20px 30px;
  min-width: 400px;
  max-width: 600px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(10px);
}

.upload-progress-modal h6 {
  margin-bottom: 8px;
  font-weight: 600;
  color: #fff;
}

.upload-progress-modal small {
  color: rgba(255, 255, 255, 0.8);
}

.upload-progress-modal .progress {
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow: hidden;
}
</style>
